import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ResponsiveLine, Serie } from '@nivo/line';
import fetchData from '../../helpers/fetchData';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import { formatNumber } from '../../helpers/formatNumber';
import { useChartTheme } from '../../themes/chartTheme';

interface ReportDateRow {
  thema: string;
  umsatz: number;
  lead_price: number;
  leads: number;
  datum: string;
}

interface ReportDetailModalProps {
  open: boolean;
  onClose: () => void;
  thema: string | null;
  startDate: Date | null;
  endDate: Date | null;
  network: string | string[];
  verlag: string[];
  /** 'all' | 'intern' | 'extern' — mirrors the segment toggle on the page. */
  segment?: string;
}

const ReportDetailModal: React.FC<ReportDetailModalProps> = ({
  open,
  onClose,
  thema,
  startDate,
  endDate,
  network,
  verlag,
  segment = 'all',
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ReportDateRow[]>([]);
  const chartTheme = useChartTheme();

  useEffect(() => {
    if (!open || !thema || !startDate || !endDate) return;

    let cancelled = false;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const networks = Array.isArray(network) ? network : [network];
        const verlags = Array.isArray(verlag) ? verlag : [];
        const result = (await fetchData('/api/vnr/sold-leads-summary', {
          start_date: dateToSqlDate(startDate),
          end_date: dateToSqlDate(endDate),
          grouping: 'report_date',
          thema: thema,
          network: networks,
          verlag: verlags,
          ...(segment === 'all' ? {} : { segment }),
        })) as ReportDateRow[];

        if (!cancelled) {
          setRows(result);
        }
      } catch (error) {
        console.error('Failed to fetch report date details:', error);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [open, thema, startDate, endDate, network, verlag, segment]);

  const summary = useMemo(() => {
    const totalUmsatz = rows.reduce((sum, row) => sum + (Number(row.umsatz) || 0), 0);
    const totalLeads = rows.reduce((sum, row) => sum + (Number(row.leads) || 0), 0);
    return {
      umsatz: totalUmsatz,
      lead_price: totalLeads > 0 ? totalUmsatz / totalLeads : 0,
      leads: totalLeads,
    };
  }, [rows]);

  const { chartData, scale } = useMemo(() => {
    if (!rows.length) return { chartData: [] as Serie[], scale: 1 };

    const sorted = [...rows].sort((a, b) => a.datum.localeCompare(b.datum));
    const maxUmsatz = Math.max(...sorted.map((d) => Number(d.umsatz) || 0));
    const maxLeads = Math.max(...sorted.map((d) => Number(d.leads) || 0));
    const s = maxUmsatz / (maxLeads || 1);

    return {
      chartData: [
        {
          id: 'Umsatz',
          data: sorted.map((d) => ({ x: d.datum, y: Number(d.umsatz) || 0 })),
        },
        {
          id: 'Leads',
          data: sorted.map((d) => ({ x: d.datum, y: (Number(d.leads) || 0) * s })),
        },
      ] as Serie[],
      scale: s,
    };
  }, [rows]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="600">
            {thema} — Detailansicht nach Datum
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" py={2}>
            Keine Daten für diesen Report im gewählten Zeitraum.
          </Typography>
        ) : (
          <>
            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{ backgroundColor: '#ffffff', maxHeight: 400, mb: 3 }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      Datum
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                      align="right"
                    >
                      Umsatz
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                      align="right"
                    >
                      Lead Preis
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                      align="right"
                    >
                      Leads
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}
                    >
                      <TableCell sx={{ color: 'black' }}>{row.datum}</TableCell>
                      <TableCell sx={{ color: 'black' }} align="right">
                        {formatNumber(Number(row.umsatz), 2)} €
                      </TableCell>
                      <TableCell sx={{ color: 'black' }} align="right">
                        {formatNumber(Number(row.lead_price), 2)} €
                      </TableCell>
                      <TableCell sx={{ color: 'black' }} align="right">
                        {formatNumber(Number(row.leads), 0)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Summary row */}
                  <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }}>Insgesamt</TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">
                      {formatNumber(summary.umsatz, 2)} €
                    </TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">
                      {formatNumber(summary.lead_price, 2)} €
                    </TableCell>
                    <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">
                      {formatNumber(summary.leads, 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Chart */}
            {chartData.length > 0 && (
              <Box height={300}>
                <ResponsiveLine
                  data={chartData}
                  theme={chartTheme}
                  margin={{ top: 20, right: 60, bottom: 50, left: 70 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 0, stacked: false }}
                  axisBottom={{
                    tickRotation: -45,
                    legend: 'Datum',
                    legendOffset: 42,
                    legendPosition: 'middle',
                  }}
                  axisLeft={{
                    legend: 'Umsatz (€)',
                    legendOffset: -60,
                    legendPosition: 'middle',
                    format: (v) => formatNumber(Number(v), 0),
                  }}
                  axisRight={{
                    legend: 'Leads',
                    legendOffset: 50,
                    legendPosition: 'middle',
                    format: (v) => formatNumber(Math.round(Number(v) / scale), 0),
                  }}
                  colors={['#4caf50', '#2196f3']}
                  pointSize={6}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  enableSlices="x"
                  sliceTooltip={({ slice }) => (
                    <div
                      style={{
                        background: 'white',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                      }}
                    >
                      <strong>{String(slice.points[0]?.data.x)}</strong>
                      {slice.points.map((point) => {
                        const isLeads = point.serieId === 'Leads';
                        const displayValue = isLeads
                          ? Math.round(Number(point.data.y) / scale)
                          : Number(point.data.y);
                        return (
                          <div key={point.id} style={{ color: point.serieColor, marginTop: 4 }}>
                            {point.serieId}:{' '}
                            {isLeads
                              ? formatNumber(displayValue, 0)
                              : formatNumber(displayValue, 2) + ' €'}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  legends={[
                    {
                      anchor: 'top',
                      direction: 'row',
                      translateY: -20,
                      itemWidth: 80,
                      itemHeight: 20,
                      symbolSize: 12,
                      symbolShape: 'circle',
                    },
                  ]}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportDetailModal;
